import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const NiceGuy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="The Nice Guy Trap | Rob Lasa"
        description="Being nice almost killed me. The difference between niceness and kindness — and what it cost me to figure that out."
        keywords="nice guy syndrome, people pleasing, niceness vs kindness, no more mr nice guy, men's mental health"
        url="/nice-guy"
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-90" />
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-display font-bold text-primary-foreground mb-6">
              Being Nice Almost Killed Me
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/90 max-w-2xl mx-auto">
              Niceness isn't kindness. One is a mask. The other requires honesty.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-8">
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                I was the nice guy. The one everyone liked. The one who never caused problems. 
                The one who said yes when he meant no, who smiled when he was drowning, who 
                measured his entire worth by how well he could manage other people's perceptions of him.
              </p>
              <p>
                It looked like generosity. It felt like survival.
              </p>
              <p className="text-xl font-semibold text-foreground border-l-4 border-accent pl-6 py-4 bg-muted/30">
                Niceness is about managing other people's perception of you. Kindness is about telling the truth — even when it's uncomfortable.
              </p>
              <p>
                I avoided conflict like it was death. I swallowed my needs so often I forgot I had them. 
                I built my entire identity around being easy to be around — and the cost was that 
                nobody actually knew me. Including me.
              </p>
              <p>
                The nice guy trap isn't about being a bad person. It's about being so afraid of 
                being seen as one that you erase yourself in the process.
              </p>
              <p>
                I lost my voice. I lost my boundaries. I lost the ability to know what I actually 
                wanted because I'd spent so long performing what everyone else wanted.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-display font-bold text-foreground mb-6">
                What Shifted
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Reading <em>No More Mr. Nice Guy</em> by Robert Glover was the first time someone 
                  described my life back to me. It was uncomfortable. It was necessary.
                </p>
                <p>
                  I started saying no. I started being honest about what I needed. I started having 
                  conversations I'd been avoiding for years. It wasn't pretty. But it was real.
                </p>
                <p>
                  I'm still in the middle of it. I still catch the nice guy reflex — the urge to 
                  perform, to manage, to say what I think someone wants to hear. But now I notice it. 
                  And that's the difference.
                </p>
              </div>
            </div>

            {/* Assessment Link */}
            <div className="bg-muted/30 rounded-lg p-8 border-l-4 border-accent">
              <h3 className="text-2xl font-display font-bold text-foreground mb-3">
                The Nice Guy Assessment
              </h3>
              <p className="text-muted-foreground mb-6">
                A starting point. Not a diagnosis. Just a mirror.
              </p>
              <Button asChild className="bg-accent text-white hover:bg-accent/90">
                <Link to="/nice-guy-assessment">
                  Take the Assessment
                </Link>
              </Button>
            </div>

            {/* Book Recommendation */}
            <div className="bg-card rounded-lg p-8 border border-border">
              <h3 className="text-2xl font-display font-bold text-foreground mb-3">
                The Book That Started It
              </h3>
              <p className="text-muted-foreground mb-4">
                <em>No More Mr. Nice Guy</em> by Robert Glover. If anything on this page 
                sounded familiar, read this book.
              </p>
              <Button asChild variant="outline">
                <a href="https://www.amazon.com/No-More-Mr-Nice-Guy/dp/0762415339" target="_blank" rel="noopener noreferrer">
                  Find the Book
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </Button>
            </div>

            {/* Closing */}
            <div className="text-center pt-8">
              <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                If you read this and thought "that's me" — sit with that for a minute. 
                You don't have to do anything about it right now. Just notice it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NiceGuy;
